import _ from 'lodash';
import hotkeys, { KeyHandler, HotkeysEvent } from 'hotkeys-js';
import mousetrap from 'mousetrap';
import React, { useEffect, useContext, useCallback, useState, useMemo } from 'react';
import {
  ShortcutType,
  ShortcutGroupMessage,
  DefaultTriggers,
  DISABLED_TRIGGER,
} from '@scale/shared/types/preference';

export interface IShortcut {
  type?: ShortcutType;
  trigger: string;
  disabled?: boolean;
  description?: string;
  action: (e: any) => any;
  disableAll: boolean;
  groupMessage?: ShortcutGroupMessage;
  forceNotEditable?: boolean;
}

export enum ShortcutMode {
  Labeler = 'labeler',
  Selector = 'selector',
  Attributes = 'attributes',
}

interface IShortcutsContext {
  shortcuts: IShortcut[];
  removeShortcut: (trigger: string, type?: ShortcutType) => void;
  addShortcut: (
    trigger: string,
    description?: string,
    type?: ShortcutType,
    groupMessage?: ShortcutGroupMessage,
    forceNotEditable?: boolean,
  ) => void;
  overrides: { [type in ShortcutType]?: string };
  addOverrides: (newOverrides: { [type in ShortcutType]?: string }) => void;
  restoreOverrides: (newOverrides: { [type in ShortcutType]?: string }) => void;
  setDisableAll: (disabled: boolean) => void;
  disableAll: boolean;
  getTrigger: (type: ShortcutType) => string;
  restored: boolean;
  setRestored: (restored: boolean) => void;
  shortcutMode: ShortcutMode;
  setShortcutMode: (mode: ShortcutMode) => void;
}

export const ShortcutsContext = React.createContext<IShortcutsContext>({
  shortcuts: [],
  addShortcut: () => {},
  removeShortcut: () => {},
  overrides: {},
  addOverrides: () => {},
  restoreOverrides: () => {},
  disableAll: false,
  setDisableAll: () => {},
  getTrigger: () => '',
  restored: false,
  setRestored: () => {},
  shortcutMode: ShortcutMode.Labeler,
  setShortcutMode: () => {},
});

export const ShortcutsProvider = ({ children }: { children: $FIXME }) => {
  const [shortcuts, setShortcuts] = useState<any[]>([]);
  const [overrides, setOverrides] = useState<{ [type in ShortcutType]?: string }>({});
  const [disableAll, setDisableAll] = useState<boolean>(false);
  const [restored, setRestored] = useState<boolean>(false);
  const [shortcutMode, setShortcutMode] = useState<ShortcutMode>(ShortcutMode.Labeler);

  const addShortcut = useCallback(
    (trigger: any, description: any, type: any, groupMessage: any, forceNotEditable: any) => {
      setShortcuts(p => [...p, { trigger, description, type, groupMessage, forceNotEditable }]);
    },
    [],
  );

  const removeShortcut = useCallback((trigger: any, type: any) => {
    if (trigger !== DISABLED_TRIGGER) {
      setShortcuts(p =>
        p.filter(s => !_.isEqual(s.trigger, trigger) && (!type || !_.isEqual(s.type, type))),
      );
    }
  }, []);

  const restoreOverrides = (newOverrides: { [type in ShortcutType]?: string }) => {
    setOverrides(newOverrides);
  };

  const addOverrides = (newOverrides: { [type in ShortcutType]?: string }) => {
    setOverrides({ ...overrides, ...newOverrides });
  };

  const getTrigger = (type: ShortcutType) => {
    const defaultTrigger = DefaultTriggers[type];
    const shortcut = shortcuts.filter(shortcut => shortcut.type && shortcut.type === type);
    const trigger = shortcut.length === 1 ? shortcut[0].trigger : defaultTrigger;
    return trigger === DISABLED_TRIGGER ? '' : trigger;
  };

  return (
    <ShortcutsContext.Provider
      value={{
        shortcuts,
        addShortcut,
        removeShortcut,
        overrides,
        addOverrides,
        restoreOverrides,
        disableAll,
        setDisableAll,
        getTrigger,
        restored,
        setRestored,
        shortcutMode,
        setShortcutMode,
      }}
    >
      {children}
    </ShortcutsContext.Provider>
  );
};
const SPECIAL_HOTKEYS = ['?', '+', 'shift+='] as const;

export interface UseShortcutOpts {
  trigger: string;
  action: KeyHandler | undefined;
  scope?: string;
  keyup?: boolean;
  keydown?: boolean;
  description?: string;
  disabled?: boolean;
  splitKey?: string;
  type?: ShortcutType;
  group?: ShortcutGroupMessage;
  preventDefault?: boolean;
  mode?: ShortcutMode;
  forceNotEditable?: boolean;
  filterInput?: boolean;
}

const useShortcut = ({
  trigger,
  action,
  scope,
  keyup,
  keydown,
  disabled,
  description,
  splitKey,
  type,
  group,
  preventDefault,
  mode,
  forceNotEditable,
  filterInput,
}: UseShortcutOpts) => {
  const context = useContext(ShortcutsContext);

  let wrappedAction: KeyHandler | undefined;
  if (action) {
    if (preventDefault) {
      wrappedAction = (e: KeyboardEvent, he: HotkeysEvent) => {
        e.preventDefault();
        e.stopPropagation();
        action(e, he);
      };
    } else {
      wrappedAction = action;
    }
  }

  const [aliasedTrigger, custom, shortcutMode] = useMemo(() => {
    let triggerWithOverride = '';
    let custom = false;
    if (context.restored) {
      triggerWithOverride = type ? DefaultTriggers[type] ?? trigger : trigger;
    } else {
      triggerWithOverride = type
        ? context.overrides[type] ?? DefaultTriggers[type] ?? trigger
        : trigger;
      custom = !!type && !!context.overrides[type];
    }
    const triggers = triggerWithOverride.toLowerCase().split(',');

    const isMac = navigator.platform.toLowerCase().includes('mac');

    const aliasedTriggers = triggers.map(t => {
      const trimmed = t.trim();
      if (isMac) return trimmed.replace('ctrl', 'cmd');
      return trimmed.replace('cmd', 'ctrl');
    });

    const shortcutMode = mode ?? ShortcutMode.Labeler;

    return [aliasedTriggers.join(', '), custom, shortcutMode];
  }, [trigger, context.overrides, context.restored]);

  const specialCallback = useCallback(
    (e: KeyboardEvent, he: HotkeysEvent) => {
      if (!wrappedAction) return null;

      for (const hotkey of SPECIAL_HOTKEYS) {
        if (hotkey === e.key) return wrappedAction(e, he);
      }
      return null;
    },
    [wrappedAction],
  );

  useEffect(() => {
    if (!disabled && context.shortcutMode === shortcutMode) {
      context.removeShortcut(aliasedTrigger, type);
      context.addShortcut(aliasedTrigger, description, type, group, forceNotEditable);
      return () => context.removeShortcut(aliasedTrigger, type);
    }
  }, [aliasedTrigger, description, disabled, context.shortcutMode, type, forceNotEditable]);

  useEffect(() => {
    const isSpecialChar = _.includes(SPECIAL_HOTKEYS, aliasedTrigger);
    const hotkey = isSpecialChar ? '*' : aliasedTrigger;
    const callback = isSpecialChar ? specialCallback : wrappedAction;
    const options = { scope, keyup, keydown, splitKey };
    if (
      !context.disableAll &&
      !disabled &&
      aliasedTrigger &&
      aliasedTrigger !== DISABLED_TRIGGER &&
      callback &&
      context.shortcutMode === shortcutMode
    ) {
      if (custom && !isSpecialChar && wrappedAction) {
        const mousetrapHotkey = hotkey.replace('cmd', 'command');
        mousetrap.bind(mousetrapHotkey, wrappedAction as () => {});
        return () => mousetrap.unbind(mousetrapHotkey);
      } else {
        // we use hotkeysJs, which takes in a string. this is diff from usekey
        hotkeys(hotkey, options, callback);
        if (filterInput) {
          hotkeys.filter = function (event: KeyboardEvent) {
            const target = event.target as HTMLElement;
            const tagName = target.tagName;
            return !(target.isContentEditable || tagName === 'SELECT' || tagName === 'TEXTAREA');
          };
        }

        return () => hotkeys.unbind(hotkey, callback);
      }
    }
  }, [
    aliasedTrigger,
    wrappedAction,
    scope,
    keyup,
    keydown,
    specialCallback,
    disabled,
    splitKey,
    context.disableAll,
    custom,
    context.shortcutMode,
    shortcutMode,
    filterInput,
  ]);
};

export default useShortcut;

export const useShortcuts = (shortcuts: UseShortcutOpts[]) => {
  for (const shortcut of shortcuts) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useShortcut(shortcut);
  }
};
